import { UserRoundSearch, MessageCircle, User } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/router';
import { useToast } from '@/hooks/use-toast';

/**
 * 모바일 네비게이션 컴포넌트
 * @param props.activeItem 현재 활성화된 메뉴 아이템
 */
interface MobileNavProps {
  activeItem?: string;
}

export function MobileNav({ activeItem }: MobileNavProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const { toast } = useToast();

  const isActive = (path: string): boolean => {
    if (activeItem) {
      return activeItem === path.replace('/', '');
    }
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavigation = (path: string, requiresAuth: boolean): void => {
    if (requiresAuth && !session) {
      toast({
        title: "로그인 필요",
        description: "이 기능을 이용하려면 로그인이 필요합니다.",
      });
      navigate('/auth');
      return;
    }
    navigate(path);
  };

  // 현재 경로가 instant-chat인 경우 모바일 네비게이션 숨김
  if (location.pathname === '/instant-chat' || location.pathname === '/') {
    return null;
  }

  const navItems = [
    {
      icon: UserRoundSearch,
      label: '계정찾기',
      path: '/search',
      id: 'search',
      requiresAuth: false
    },
    {
      icon: MessageCircle,
      label: '채팅',
      path: '/chat',
      id: 'chat',
      requiresAuth: true
    },
    {
      icon: User,
      label: session ? '프로필' : '로그인',
      path: session ? '/profile' : '/auth',
      id: 'profile',
      requiresAuth: false
    }
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-lg supports-[backdrop-filter]:bg-background/60 ">
      <nav className="container">
        <div className="flex justify-around h-16 grid-cols-5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = activeItem ? activeItem === item.id : isActive(item.path);

            return (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path, item.requiresAuth)}
                className={cn(
                  'flex flex-col items-center justify-center gap-1 h-full flex-1 px-2 py-3 rounded-none transition-colors duration-200 ease-in-out',
                  'hover:text-foreground hover:bg-muted',
                  'active:text-primary/80 active:bg-muted/80',
                  active
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground'
                )}
              >
                <div className="relative">
                  <Icon className={cn(
                    'h-5 w-5 transition-all',
                    active && 'scale-110'
                  )} />
                </div>
                <span className={cn(
                  'text-xs font-medium',
                  active ? 'text-primary font-semibold' : ''
                )}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}